import PricingStrategyService from "@/services/PricingStrategyService";
import Vue from "vue";

// Guardarail y regla son la misma entidad con distinto rule_kind, pero cada
// una tiene sus propios endpoints y su propio dataset.
const isGuardrail = params => (params && params.rule_kind) === "guardrail";

export const state = {
  // status
  strategies_status: 0,
  strategy_status: 0,
  strategy_room_types_status: 0,
  strategy_rates_status: 0,
  rules_status: 0,
  rule_types_status: 0,
  rule_templates_status: 0,
  guardrails_status: 0,
  guardrail_templates_status: 0,
  anticipation_slots_status: 0,
  planning_status: 0,
  recommendations_status: 0,
  strategy_config_status: 0,

  // dataset
  strategies: null,
  strategy: null,
  strategy_room_types: null,
  strategy_rates: null,
  rules: null,
  rule_types: null,
  rule_templates: null,
  // Los guardarailes son la misma entidad que las reglas (rule_kind los
  // discrimina) pero viven en su propio dataset: /get-rules no los devuelve,
  // así que ningún consumidor de state.rules tiene que filtrar.
  guardrails: null,
  guardrail_templates: null,
  anticipation_slots: null,
  planning: null,
  recommendations: null,
  strategy_config: null,

  // ui selectors
  strategy_id: null,
  rule_definition_dialog: false,
  rule_definition_info_dialog: false,
  rule_templates_dialog: false,
  rule_to_edit: null,
  rule_to_place: null,
  rule_info: null,
  // "rule" | "guardrail": en qué modo se abrieron el canvas y el catálogo
  rule_definition_mode: "rule",
  rule_templates_kind: "rule",
  set_strategy_dialog: false,
  strategy_to_edit: null,
  current_rate_plan_id: null,
  planning_from_date: null,
  planning_to_date: null,
  planning_room_type_id: null,
  planning_rate_plan_id: null,
  recommendations_from_date: null,
  recommendations_to_date: null,
  recommendations_strategy_id: null,
  recommendations_rate_plan_id: null
};

export const mutations = {
  GET_DATASET(state, { dataset, data }) {
    state[dataset] = data;
  },
  SET_DATASET_STATUS(state, { dataset, status }) {
    state[dataset + "_status"] = status;
  },
  SET_STRATEGIES(state, v) {
    state.strategies = v;
  },
  SET_STRATEGY_RATES(state, v) {
    state.strategy_rates = v;
  },
  ADD_STRATEGY(state, v) {
    state.strategies.push(v);
  },
  SET_STRATEGY(state, v) {
    // console.log("🔄 SET_STRATEGY", v);
    state.strategy = v;
  },
  DELETE_STRATEGY(state, v) {
    state.strategies = state.strategies.filter(strategy => strategy.id !== v);
  },
  // La escritura de reglas y guardarailes comparte endpoint (/set-rule), pero
  // cada tipo tiene su dataset: rule_kind decide en cuál cae el resultado.
  ADD_RULE(state, v) {
    const dataset = v.rule_kind === "guardrail" ? "guardrails" : "rules";
    if (!state[dataset]) state[dataset] = [];
    state[dataset].push(v);
  },
  SET_RULE(state, v) {
    const dataset = v.rule_kind === "guardrail" ? "guardrails" : "rules";
    state[dataset] = (state[dataset] || []).map(rule =>
      rule.id === v.id ? v : rule
    );
  },
  SET_STRATEGY_ID(state, v) {
    state.strategy_id = v;
  },
  SET_RULE_TO_EDIT(state, v) {
    state.rule_to_edit = v;
  },
  SET_RULE_INFO(state, v) {
    state.rule_info = v;
  },
  SET_RULE_DEFINITION_DIALOG(state, v) {
    state.rule_definition_dialog = v;
  },
  SET_RULE_DEFINITION_INFO_DIALOG(state, v) {
    state.rule_definition_info_dialog = v;
  },
  SET_RULE_TEMPLATES_DIALOG(state, v) {
    state.rule_templates_dialog = v;
  },
  SET_RULE_TO_PLACE(state, v) {
    state.rule_to_place = v;
  },
  DELETE_RULE(state, { rule_id, rule_kind }) {
    const dataset = rule_kind === "guardrail" ? "guardrails" : "rules";
    state[dataset] = (state[dataset] || []).filter(rule => rule.id !== rule_id);
  },
  SET_RULE_DEFINITION_MODE(state, v) {
    state.rule_definition_mode = v;
  },
  SET_RULE_TEMPLATES_KIND(state, v) {
    state.rule_templates_kind = v;
  },
  SET_SET_STRATEGY_DIALOG(state, v) {
    state.set_strategy_dialog = v;
  },
  SET_STRATEGY_TO_EDIT(state, v) {
    state.strategy_to_edit = v;
  },
  SET_PLANNING_FROM_DATE(state, v) {
    state.planning_from_date = v;
  },
  SET_PLANNING_TO_DATE(state, v) {
    state.planning_to_date = v;
  },
  SET_PLANNING_ROOM_TYPE_ID(state, v) {
    state.planning_room_type_id = v;
  },
  SET_PLANNING_RATE_PLAN_ID(state, v) {
    state.planning_rate_plan_id = v;
  },
  SET_PLANNING_DAY_STRATEGY(state, params) {
    if (state.planning[params.date]) {
      const strategy = state.strategies.find(
        item => item.id === params.strategy_id
      );
      if (strategy) {
        state.planning[params.date].strategies = [strategy];
      } else {
        state.planning[params.date].strategies = [];
      }
    }
  },
  SET_RECOMMENDATIONS_FROM_DATE(state, v) {
    state.recommendations_from_date = v;
  },
  SET_RECOMMENDATIONS_TO_DATE(state, v) {
    state.recommendations_to_date = v;
  },
  SET_RECOMMENDATIONS_RATE_PLAN_ID(state, v) {
    state.recommendations_rate_plan_id = v;
  },
  SET_RECOMMENDATIONS_STRATEGY_ID(state, v) {
    state.recommendations_strategy_id = v;
  },
  SET_RECOMMENDATION_APPLIED(state, v) {
    if (!state.recommendations) {
      return;
    }
    state.recommendations = state.recommendations.map(item => {
      // Buscar si este item está en el array de aplicados
      const appliedItem = v.find(applied => applied.id === item.id);
      return appliedItem
        ? { ...item, status: "applied", price_applied: appliedItem.price }
        : item;
    });
  },
  SET_RECOMMENDATIONS_IGNORED(state, v) {
    if (!state.recommendations) {
      return;
    }
    state.recommendations = state.recommendations.map(item => {
      const ignoredItem = v.find(ignored => ignored.id === item.id);
      return ignoredItem ? { ...item, status: "ignored" } : item;
    });
  },
  SET_RECOMMENDATIONS_PENDING(state, v) {
    if (!state.recommendations) {
      return;
    }
    state.recommendations = state.recommendations.map(item => {
      const pendingItem = v.find(pending => pending.id === item.id);
      return pendingItem ? { ...item, status: "pending" } : item;
    });
  }
};

export const actions = {
  fetchPricingstrategyStrategies({ commit, rootGetters }, p) {
    const dataset = "strategies";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id
    };
    PricingStrategyService.getStrategies(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyStrategyRates({ commit, rootGetters }, p) {
    const dataset = "strategy_rates";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id
    };
    PricingStrategyService.getStrategyRates(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyAnticipationSlots({ commit, rootGetters }, p) {
    const dataset = "anticipation_slots";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id
    };
    PricingStrategyService.getAnticipationSlots(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyStrategyRoomTypes({ commit, rootGetters }, p) {
    const dataset = "strategy_room_types";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id
    };
    PricingStrategyService.getStrategyRoomTypes(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyStrategy({ commit, rootGetters }, p) {
    const dataset = "strategy";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (!state.strategy_id) {
      return null;
    }
    let params = {
      strategy_id: state.strategy_id
    };
    PricingStrategyService.getStrategy(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyPlanning({ commit, rootGetters }, p) {
    const dataset = "planning";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (
      !rootGetters.current_hotel ||
      !rootGetters.current_hotel.id ||
      !state.planning_from_date ||
      !state.planning_to_date ||
      !state.planning_room_type_id ||
      !state.planning_rate_plan_id
    ) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id,
      from_date: state.planning_from_date,
      to_date: state.planning_to_date,
      room_type_id: state.planning_room_type_id,
      rate_plan_id: state.planning_rate_plan_id
    };
    PricingStrategyService.getPlanning(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  fetchPricingstrategyRecommendations({ commit, rootGetters, state }, p) {
    const dataset = "recommendations";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });
    if (
      !rootGetters.current_hotel ||
      !rootGetters.current_hotel.id ||
      !state.recommendations_from_date ||
      !state.recommendations_to_date
    ) {
      return null;
    }
    let params = {
      hotel_id: rootGetters.current_hotel.id
    };
    if (state.recommendations_from_date) {
      params.from_date = state.recommendations_from_date;
    }
    if (state.recommendations_to_date) {
      params.to_date = state.recommendations_to_date;
    }
    if (state.recommendations_rate_plan_id) {
      params.rate_plan_id = state.recommendations_rate_plan_id;
    }
    if (state.recommendations_strategy_id) {
      params.strategy_id = state.recommendations_strategy_id;
    }
    PricingStrategyService.getSuggestions(params).then(response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    });
  },
  setPricingStrategyStrategy({ commit }, params) {
    return PricingStrategyService.setStrategy(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_STRATEGY", response.data);
      return response;
    });
  },
  deletePricingStrategyStrategy({ commit }, params) {
    return PricingStrategyService.deleteStrategy(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("DELETE_STRATEGY", params.strategy_id);
      return response;
    });
  },
  setPricingStrategyStrategyId({ commit }, v) {
    commit("SET_STRATEGY_ID", v);
  },
  setPricingStrategyRuleToEdit({ commit }, v) {
    commit("SET_RULE_TO_EDIT", v);
  },
  setPricingStrategyRuleInfo({ commit }, v) {
    commit("SET_RULE_INFO", v);
  },
  setPricingStrategyRuleDefinitionDialog({ commit }, v) {
    commit("SET_RULE_DEFINITION_DIALOG", v);
  },
  setPricingStrategyRuleDefinitionInfoDialog({ commit }, v) {
    commit("SET_RULE_DEFINITION_INFO_DIALOG", v);
  },
  setPricingStrategyStrategyRule({ commit }, params) {
    return PricingStrategyService.setStrategyRule(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_STRATEGY", response.data);
      return response;
    });
  },
  deletePricingStrategyStrategyRule({ commit }, params) {
    return PricingStrategyService.deleteStrategyRule(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_STRATEGY", response.data);
      return response;
    });
  },
  setPricingStrategySetStrategyDialog({ commit }, v) {
    commit("SET_SET_STRATEGY_DIALOG", v);
  },
  setPricingStrategyStrategyToEdit({ commit }, v) {
    commit("SET_STRATEGY_TO_EDIT", v);
  },
  setPricingStrategyPlanningFromDate({ commit }, v) {
    commit("SET_PLANNING_FROM_DATE", v);
  },
  setPricingStrategyPlanningToDate({ commit }, v) {
    commit("SET_PLANNING_TO_DATE", v);
  },
  setPricingStrategyRecommendationsFromDate({ commit }, v) {
    commit("SET_RECOMMENDATIONS_FROM_DATE", v);
  },
  setPricingStrategyRecommendationsToDate({ commit }, v) {
    commit("SET_RECOMMENDATIONS_TO_DATE", v);
  },
  setPricingStrategyRecommendationsRatePlanId({ commit }, v) {
    commit("SET_RECOMMENDATIONS_RATE_PLAN_ID", v);
  },
  setPricingStrategyRecommendationsStrategyId({ commit }, v) {
    commit("SET_RECOMMENDATIONS_STRATEGY_ID", v);
  },
  setPricingStrategyPlanningRoomTypeId({ commit, rootGetters, dispatch }, v) {
    commit("SET_PLANNING_ROOM_TYPE_ID", v);

    if (rootGetters.current_hotel && rootGetters.current_hotel.id) {
      const user_settings = Object.assign({}, rootGetters.getUserSettings);

      const room_type_storage =
        user_settings && user_settings["pricingstrategy_planning_room_type"]
          ? user_settings["pricingstrategy_planning_room_type"]
          : {};

      if (!room_type_storage[rootGetters.current_hotel.id]) {
        room_type_storage[rootGetters.current_hotel.id] = {};
      }

      room_type_storage[rootGetters.current_hotel.id] = v;
      user_settings["pricingstrategy_planning_room_type"] = room_type_storage;
      dispatch("setUserSettings", user_settings);
    }
  },
  setPricingStrategyPlanningRatePlanId({ commit, rootGetters, dispatch }, v) {
    commit("SET_PLANNING_RATE_PLAN_ID", v);

    if (rootGetters.current_hotel && rootGetters.current_hotel.id) {
      const user_settings = Object.assign({}, rootGetters.getUserSettings);

      const rate_plan_storage =
        user_settings && user_settings["pricingstrategy_planning_rate_plan"]
          ? user_settings["pricingstrategy_planning_rate_plan"]
          : {};

      if (!rate_plan_storage[rootGetters.current_hotel.id]) {
        rate_plan_storage[rootGetters.current_hotel.id] = {};
      }

      rate_plan_storage[rootGetters.current_hotel.id] = v;
      user_settings["pricingstrategy_planning_rate_plan"] = rate_plan_storage;
      dispatch("setUserSettings", user_settings);
    }
  },
  setPricingStrategyPlanning({ commit }, params) {
    return PricingStrategyService.setPlanning(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_PLANNING_DAY_STRATEGY", params);
      return response;
    });
  },
  setPricingStrategyPlanningPeriod({ commit }, params) {
    return PricingStrategyService.setPlanning(params).then(response => {
      if (response.error) throw new Error(response.error);
      // Para cada uno de los dias desde from_date a to_date, se aplica la estrategia
      const from_date = Vue.moment(params.from_date);
      const to_date = Vue.moment(params.to_date);
      for (
        let date = from_date.clone();
        date.isSameOrBefore(to_date);
        date.add(1, "day")
      ) {
        // Solo aplicar en los días de la semana especificados
        if (!params.weekdays || params.weekdays.includes(date.day())) {
          commit("SET_PLANNING_DAY_STRATEGY", {
            date: Vue.moment(date).format("YYYY-MM-DD"),
            strategy_id: params.strategy_id
          });
        }
      }
      return response;
    });
  },
  applyPricingStrategySuggestions({ commit }, params) {
    return PricingStrategyService.applySuggestions(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_RECOMMENDATION_APPLIED", params.suggestions);
      commit("SET_DAILY_SUGGESTIONS_APPLIED", params.suggestions);
      return response;
    });
  },
  deletePricingStrategyStrategyRow({ commit }, params) {
    return PricingStrategyService.deleteStrategyRow(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_STRATEGY", response.data);
      return response;
    });
  },
  ignorePricingStrategySuggestion({ commit }, params) {
    return PricingStrategyService.ignoreSuggestion(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_RECOMMENDATIONS_IGNORED", params.suggestions);
      commit("SET_DAILY_SUGGESTIONS_IGNORED", params.suggestions);
      return response;
    });
  },
  reactivatePricingStrategySuggestion({ commit }, params) {
    return PricingStrategyService.reactivateSuggestion(params).then(
      response => {
        if (response.error) throw new Error(response.error);
        commit("SET_RECOMMENDATIONS_PENDING", params.suggestions);
        commit("SET_DAILY_SUGGESTIONS_PENDING", params.suggestions);
        return response;
      }
    );
  },

  // El filtro de fechas de la pantalla es de fecha de ejecución (date), no de
  // fecha objetivo (target_date), así que NO se pasa aquí: el backend elige las
  // fechas objetivo a partir del calendario de estrategias.
  generatePricingStrategyFakeSuggestions(
    { dispatch, rootGetters },
    params = {}
  ) {
    const payload = {
      hotel_id: rootGetters.current_hotel.id,
      ...params
    };
    return PricingStrategyService.generateFakeSuggestions(payload).then(
      response => {
        if (response.error) throw new Error(response.error);
        dispatch("fetchPricingstrategyRecommendations");
        return response;
      }
    );
  },

  // Evalúa una regla sin guardar contra los datos reales de una fecha. No
  // toca la base de datos, así que no hace falta refrescar nada después.
  testPricingStrategyRule({ rootGetters }, params = {}) {
    const payload = {
      hotel_id: rootGetters.current_hotel.id,
      ...params
    };
    return PricingStrategyService.testStrategyRule(payload).then(response => {
      if (response.error) throw new Error(response.error);
      return response;
    });
  },

  fetchPricingstrategyStrategyConfig({ commit, rootGetters }) {
    const dataset = "strategy_config";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    const params = { hotel_id: rootGetters.current_hotel.id };
    return PricingStrategyService.getStrategyConfig(params).then(response => {
      commit("GET_DATASET", { dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
    });
  },
  fetchPricingstrategyRuleTypes({ commit, rootGetters }) {
    const dataset = "rule_types";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    const params = { hotel_id: rootGetters.current_hotel.id };
    return PricingStrategyService.getRuleTypes(params).then(response => {
      commit("GET_DATASET", { dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
    });
  },
  fetchPricingstrategyRules({ commit, rootGetters, state }) {
    const dataset = "rules";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    const params = { hotel_id: rootGetters.current_hotel.id };
    if (state.current_rate_plan_id)
      params.rate_plan_id = state.current_rate_plan_id;
    return PricingStrategyService.getRules(params).then(response => {
      commit("GET_DATASET", { dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
    });
  },
  fetchPricingstrategyRuleTemplates({ commit, rootGetters }) {
    const dataset = "rule_templates";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id)
      return null;
    const params = { hotel_id: rootGetters.current_hotel.id };
    return PricingStrategyService.getRuleTemplates(params).then(response => {
      commit("GET_DATASET", { dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
    });
  },
  // Los dos fetch de guardarailes tragan el error a propósito.
  //
  // Utils.status() exige que TODOS los módulos suscritos lleguen a 2 para que
  // aBaseComponent pinte el contenido. Mientras el backend no exponga
  // /get-guardrails y /get-guardrail-templates, dejar que la promesa reviente
  // congelaría la pantalla de estrategia entera en el skeleton. Con el catch,
  // el dataset queda vacío, el estado pasa a 2 y el resto de la pantalla
  // funciona con normalidad.
  fetchPricingstrategyGuardrails({ commit, rootGetters }) {
    const dataset = "guardrails";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    // Sin hotel no hay nada que pedir, pero el estado tiene que llegar a 2
    // igualmente: si se queda en 1, cualquier pantalla que suscriba el módulo
    // se congela en el skeleton para siempre.
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      commit("GET_DATASET", { dataset, data: [] });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
      return null;
    }
    const params = { hotel_id: rootGetters.current_hotel.id };
    return PricingStrategyService.getGuardrails(params)
      .then(response => {
        commit("GET_DATASET", { dataset, data: response.data });
        commit("SET_DATASET_STATUS", { dataset, status: 2 });
      })
      .catch(() => {
        commit("GET_DATASET", { dataset, data: [] });
        commit("SET_DATASET_STATUS", { dataset, status: 2 });
      });
  },
  fetchPricingstrategyGuardrailTemplates({ commit, rootGetters }) {
    const dataset = "guardrail_templates";
    commit("SET_DATASET_STATUS", { dataset, status: 1 });
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      commit("GET_DATASET", { dataset, data: { tags: [], templates: [] } });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
      return null;
    }
    const params = { hotel_id: rootGetters.current_hotel.id };
    return PricingStrategyService.getGuardrailTemplates(params)
      .then(response => {
        commit("GET_DATASET", { dataset, data: response.data });
        commit("SET_DATASET_STATUS", { dataset, status: 2 });
      })
      .catch(() => {
        commit("GET_DATASET", { dataset, data: { tags: [], templates: [] } });
        commit("SET_DATASET_STATUS", { dataset, status: 2 });
      });
  },
  setPricingStrategyStrategyGuardrail({ commit }, params) {
    return PricingStrategyService.setStrategyGuardrail(params).then(
      response => {
        if (response.error) throw new Error(response.error);
        commit("SET_STRATEGY", response.data);
        return response;
      }
    );
  },
  deletePricingStrategyStrategyGuardrail({ commit }, params) {
    return PricingStrategyService.deleteStrategyGuardrail(params).then(
      response => {
        if (response.error) throw new Error(response.error);
        commit("SET_STRATEGY", response.data);
        return response;
      }
    );
  },
  sortPricingStrategyStrategyGuardrails({ commit }, params) {
    return PricingStrategyService.sortStrategyGuardrails(params).then(
      response => {
        if (response.error) throw new Error(response.error);
        commit("SET_STRATEGY", response.data);
        return response;
      }
    );
  },
  setPricingStrategyRuleDefinitionMode({ commit }, v) {
    commit("SET_RULE_DEFINITION_MODE", v);
  },
  setPricingStrategyRuleTemplatesKind({ commit }, v) {
    commit("SET_RULE_TEMPLATES_KIND", v);
  },
  setPricingStrategyRuleTemplatesDialog({ commit }, v) {
    commit("SET_RULE_TEMPLATES_DIALOG", v);
  },
  setPricingStrategyRuleToPlace({ commit }, v) {
    commit("SET_RULE_TO_PLACE", v);
  },
  // Reglas y guardarailes tienen endpoints propios. El componente que llama
  // no tiene por qué saberlo: manda rule_kind y aquí se elige la ruta.
  addPricingStrategyRule({ commit }, params) {
    // Se llama por nombre y no con la función suelta, para no perder el
    // contexto del servicio
    const method = isGuardrail(params) ? "setGuardrail" : "setRule";
    return PricingStrategyService[method](params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("ADD_RULE", response.data);
      return response;
    });
  },
  setPricingStrategyRule({ commit, dispatch }, params) {
    // Se llama por nombre y no con la función suelta, para no perder el
    // contexto del servicio
    const method = isGuardrail(params) ? "setGuardrail" : "setRule";
    return PricingStrategyService[method](params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_RULE", response.data);
      dispatch("fetchPricingstrategyStrategy");
      return response;
    });
  },
  deletePricingStrategyRule({ commit, dispatch }, params) {
    // Se llama por nombre y no con la función suelta, para no perder el
    // contexto del servicio
    const method = isGuardrail(params) ? "deleteGuardrail" : "deleteRule";
    return PricingStrategyService[method](params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("DELETE_RULE", {
        rule_id: params.rule_id,
        rule_kind: params.rule_kind
      });
      dispatch("fetchPricingstrategyStrategy");
      return response;
    });
  },
  duplicatePricingStrategyRule({ commit }, params) {
    // Se llama por nombre y no con la función suelta, para no perder el
    // contexto del servicio
    const method = isGuardrail(params) ? "duplicateGuardrail" : "duplicateRule";
    return PricingStrategyService[method](params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("ADD_RULE", response.data);
      return response;
    });
  }
};

export const getters = {
  getPricingStrategyStrategyById: state => id => {
    return (state.strategies || []).find(item => item.id === id) || null;
  },
  getPricingStrategyRuleTypeByCode: state => code => {
    return (state.rule_types || []).find(item => item.code === code) || null;
  },
  getPricingStrategyRuleById: state => id => {
    return (state.rules || []).find(item => item.id === id) || null;
  },
  // Reglas y guardarailes son datasets distintos, pero una sugerencia solo
  // trae un rule_id: lo dispara cualquiera de los dos y quien la pinta no
  // tiene por qué saber de cuál se trata.
  getPricingStrategyRuleOrGuardrailById: state => id => {
    return (
      (state.rules || []).find(item => item.id === id) ||
      (state.guardrails || []).find(item => item.id === id) ||
      null
    );
  },
  getPricingStrategyGuardrailById: state => id => {
    return (state.guardrails || []).find(item => item.id === id) || null;
  },
  // Tipos de condición de guardarail. rule_types es una tabla única a
  // propósito: getPricingStrategyRuleTypeByCode resuelve icono y nombre de
  // cualquier condición, sea de regla o de guardarail.
  getPricingStrategyGuardrailRuleTypes: state => {
    return (state.rule_types || []).filter(
      item => item.rule_kind === "guardrail"
    );
  },
  getPricingStrategyStrategyRatePlanById: state => id => {
    return (state.strategy_rates || []).find(item => item.id === id) || null;
  },
  getPricingStrategyStrategyRoomTypeById: state => id => {
    return state.strategy_room_types.find(item => item.id === id);
  }
};
